import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { User, Users, Home, Phone, MapPin, Calendar, Hash, Building2 } from 'lucide-react';

export default function MyFamilyPage() {
  const { api, user } = useAuth();
  const [family, setFamily] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyFamily();
  }, [user]);

  const fetchMyFamily = async () => {
    if (!user?.its_id) {
      setLoading(false);
      return;
    }

    try {
      // Get family by member's ITS ID
      const response = await api.get(`/families/by-its/${user.its_id}`);
      setFamily(response.data.family);
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching family:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!family) {
    return (
      <div className="p-6">
        <Card className="max-w-lg mx-auto">
          <CardContent className="p-8 text-center">
            <User className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Family Not Found</h2>
            <p className="text-slate-500">
              Your family profile could not be found. Please contact the administrator.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading text-navy">My Family</h1>
        <Badge variant="outline" className="text-navy">
          {family.mohalla} - {family.sector}
        </Badge>
      </div>

      {/* Family Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="w-5 h-5 text-navy" />
            Family Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Head of Family</p>
              <p className="font-semibold text-slate-800">{family.hof_name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Hash className="w-3 h-3" /> HOF ITS
              </p>
              <p className="font-mono text-slate-800">{family.hof_its || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Phone className="w-3 h-3" /> Mobile
              </p>
              <p className="text-slate-800">{family.mobile || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Address
              </p>
              <p className="text-slate-800">{family.address || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Building2 className="w-3 h-3" /> Building
              </p>
              <p className="text-slate-800">{family.building_tag || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Thaali Number</p>
              <p className="font-mono text-slate-800">{family.thaali_number || '-'}</p>
            </div>
          </div>

          {/* FMB Status */}
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">FMB Status</p>
                <Badge className={family.fmb_status === 'Going' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {family.fmb_status || 'Not Set'}
                </Badge>
              </div>
              {family.fmb_reason && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Reason</p>
                  <p className="text-sm text-slate-600">{family.fmb_reason}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Family Members */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-navy" />
            Family Members ({members.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No members found</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>ITS ID</TableHead>
                  <TableHead>Relation</TableHead>
                  <TableHead>Gender</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Misaq</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">{member.full_name}</TableCell>
                    <TableCell className="font-mono text-sm">{member.its_id || '-'}</TableCell>
                    <TableCell>{member.relation || '-'}</TableCell>
                    <TableCell>{member.gender || '-'}</TableCell>
                    <TableCell>{member.age || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={member.misaq_status?.toLowerCase() === 'done' ? 'success' : 'secondary'}>
                        {member.misaq_status || 'N/A'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Distribution History - Placeholder */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-navy" />
            Distribution History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-500 text-center py-8">No distribution history available</p>
        </CardContent>
      </Card>
    </div>
  );
}
